import { memo } from 'react';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export type CurrentTimeIndicatorProps = {
  /** Minutes from midnight for "now". */
  minutes: number;
  hourHeight: number;
};

/** Compact 12-hour label used by the design, e.g. `11:45`. */
function formatNow(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')}`;
}

/**
 * The "now" line across the day timeline. Only rendered when the timeline is showing
 * today (BR-07) — the day screen decides that and simply omits this otherwise.
 */
function CurrentTimeIndicator({ minutes, hourHeight }: CurrentTimeIndicatorProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const top = (minutes / 60) * hourHeight;

  return (
    <Stack
      accessibilityRole="text"
      accessibilityLabel={`${STRINGS.guardian.kids.currentTime} ${formatNow(minutes)}`}
      direction="row"
      align="center"
      style={[styles.root, { top }]}
      pointerEvents="none">
      <Stack
        paddingY={scale(1)}
        paddingLeft={scale(4)}
        paddingRight={scale(8)}
        backgroundColor={guardianColors['Main/Main']}
        style={styles.badge}
        pointerEvents="none">
        <Text
          numberOfLines={1}
          variant="Font Guardian App/Subtitle/Subtitle 10 Bold"
          style={styles.badgeText}>
          {formatNow(minutes)}
        </Text>
      </Stack>
      <Stack flex={1} height={scale(1.5)} backgroundColor={guardianColors['Main/Main']} />
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    position: 'absolute',
    left: 0,
    right: 0,
    // Centre the badge/line on the exact minute rather than hanging below it.
    transform: [{ translateY: scale(-7) }],
  },
  badgeText: {
    color: colors['0'],
  },
  badge: {
    borderTopRightRadius: scale(100),
    borderBottomRightRadius: scale(100),
    borderCurve: 'continuous',
  },
}));

export default memo(CurrentTimeIndicator);
